import type { Ref, ComputedRef, UnwrapNestedRefs } from 'vue'
import { nextTick } from 'vue'
import { computed, ref, inject } from 'vue'

import type {
  FolderRetrieve,
  FolderFile,
  FileRetrieve,
  File,
  FolderFolder,
  Favorite,
  FileContentTypeEnum,
  Folder,
  MiniPage,
} from '@openapi'
import { orderBy, isEmpty } from 'lodash-es'
import type { RecentFile } from '@lib/types/recentFiles.types'
import { ViewCompactItemType } from '@lib/types/files'
import { SUPPORTED_IMAGE_TYPES } from '@lib/constants/uploads.const'
import { isShareable } from '@content-selector/util/file'
import type { ApiStore } from '@content-selector/stores/api'
import { useLocalStorage } from '@vueuse/core'

export type AppName = 'admin' | 'impact'
export type SelectorType =
  | 'content'
  | 'assets'
  | 'external_links'
  | 'ai_images'
  | 'pexels_images'
  | 'theme'
  | `multimedia-selector-${string}`
export type SortOption = 'nameAsc' | 'nameDesc' | 'modifiedAsc' | 'modifiedDesc'
export type SelectedPage = {
  id: string
  name?: string
  file: { id: File['id']; name: File['name'] }
  page_index: number
  permissions?: File['permissions']
  thumbnail_url?: string
  type: 'page'
}
export type SelectedFile = FolderFile & { type: 'file' }
export type SelectedFolder = FolderFolder & { type: 'folder' }
export type SelectedAsset = {
  id: File['id']
  type: 'asset'
  content_extension?: File['content_extension']
  thumbnail_url?: string
  content_url?: string
  file?: { id: File['id']; name: File['name'] }
  theme_meta?: {
    is_theme_logo?: boolean
    theme_asset_index?: number
  }
}
export type SelectedExternalLink = {
  id: string
  url: string
  thumbnail_url?: string
  type: 'external_link'
  content_type: 'video' | 'image'
  from: 'custom' | 'pexels' | 'unsplash' | 'ai'
  theme_meta?: {
    is_theme_logo?: boolean
    theme_asset_index?: number
  }
}
export type ContentSelectorSelectedItem = SelectedPage | SelectedFile | SelectedAsset | SelectedExternalLink

export type BrowserSectionType = 'all' | 'favorites' | 'recent' | 'trash'

export type FolderFileWithFolderId = File & { folder_id?: string }

export type FolderItemWithFolderId =
  | (FolderFileWithFolderId & { type: 'file' })
  | (FolderFolder & { folder_id?: string; type: 'folder' })

export type RecentFileWithFavoriteId = RecentFile & {
  favoriteId: string
}

export function isPage(item: ContentSelectorSelectedItem): item is SelectedPage {
  return item.type === 'page'
}

export function isFile(item: ContentSelectorSelectedItem): item is SelectedFile {
  return item.type === 'file'
}

export function isAsset(item: ContentSelectorSelectedItem): item is SelectedAsset {
  return item.type === 'asset'
}

export function isExternalLink(item: ContentSelectorSelectedItem): item is SelectedExternalLink {
  return item.type === 'external_link'
}

function sortItems<T extends { name?: string; modified_at?: string }>(items: T[], sortOption: SortOption): T[] {
  switch (sortOption) {
    case 'nameAsc':
    case 'nameDesc':
      return [...items].sort((a, b) => {
        const comparison = (a.name || '').localeCompare(b.name || '')
        return sortOption === 'nameAsc' ? comparison : -comparison
      })
    case 'modifiedAsc':
    case 'modifiedDesc':
      return [...items].sort((a, b) => {
        const aDate = a.modified_at ? new Date(a.modified_at).getTime() : 0
        const bDate = b.modified_at ? new Date(b.modified_at).getTime() : 0
        const comparison = aDate - bDate
        return sortOption === 'modifiedAsc' ? comparison : -comparison
      })
    default:
      return items
  }
}

export type AppStateProperties = {
  appName: Ref<AppName | ''>
  // ui
  allowedContentTypes: Ref<FileContentTypeEnum[]>
  activeSelectorType: Ref<SelectorType>
  allowedIds: Ref<File['id'][]>
  initialMetadataFilters: Ref<Record<string, any>>
  allowedOriginalExtensions: Ref<string[] | undefined>
  search: Ref<string>
  searchMetadataFilters: Ref<Record<string, any>>
  isLocalSearch: Ref<boolean>
  selectedSortOption: Ref<SortOption>
  // selection
  selectedItems: Ref<ContentSelectorSelectedItem[]>
  addedItems: Ref<ContentSelectorSelectedItem[]>
  selectedSection: Ref<BrowserSectionType>
  // computed
  isDragNDropEnabled: ComputedRef<boolean>
  selectedFiles: ComputedRef<SelectedFile[]>
  selectedPages: ComputedRef<SelectedPage[]>
  selectedAssets: ComputedRef<SelectedAsset[]>
  selectedExternalLinks: ComputedRef<SelectedExternalLink[]>
  sortedFavorites: ComputedRef<Favorite[]>
  displayedFavorites: ComputedRef<(Favorite & { favoriteId: string })[]>
  displayedRecentFiles: ComputedRef<RecentFileWithFavoriteId[]>
  showFilteredResults: ComputedRef<boolean>
  displayedFiles: ComputedRef<SelectedFile[] | SelectedFolder[] | RecentFileWithFavoriteId[] | FolderItemWithFolderId[]>
  activeFolderFolders: ComputedRef<FolderFolder[]>
  activeFolderFiles: ComputedRef<FolderFile[]>
  isAdmin: ComputedRef<boolean>
  // data
  allowMultimediaSelectorApp: Ref<boolean>
  sectionPanelRef: Ref<HTMLElement | null>
  activeFolder: Ref<FolderRetrieve | null>
  activeFile: Ref<FileRetrieve | null>
  disableNoSharable: Ref<boolean>
  disableNoPptxData: Ref<boolean>
  recent: Ref<RecentFile[]>
  favorites: Ref<Record<MiniPage['id'] | File['id'] | Folder['id'], Favorite>>
  filteredItems: Ref<FolderItemWithFolderId[]>
  allowedSelectorTypes: Ref<SelectorType[]>
}

export type AppStore = AppStateProperties & {
  // Actions
  getThumbnailUrl: (file: Partial<File>) => string | null | undefined
  setAppName: (appName: AppName) => void
  resetSelectedItems: () => void
  resetAddedItems: () => void
  setItems: (
    variable: 'added' | 'selected',
    items: ContentSelectorSelectedItem[],
    maxSelections?: number,
  ) => Promise<void>
  setActiveSelectorType: (s?: SelectorType) => void
  setAllowedSelectorTypes: (s: SelectorType[]) => void
  selectItem: (i: ContentSelectorSelectedItem) => void
  handleItemClick: (item: ContentSelectorSelectedItem) => void
  addItems: (position?: 'top' | 'bottom' | number) => void
  deleteItems: () => void
  deleteItem: (item: ContentSelectorSelectedItem) => void
  setSectionPanelRef: (element: HTMLElement | null) => void
  selectAllFiles: (selection: SelectedFile[]) => void
  setDisableNoSharable: (disable: boolean) => void
  setDisableNoPptxData: (disable: boolean) => void
  deselectAllFiles: (f: SelectedFile[] | Pick<FolderFile, 'id'>[]) => void
  deselectFile: (f: SelectedFile | Pick<FolderFile, 'id'>) => void
  deselectPage: (p: SelectedPage) => void
  deselectAsset: (a: SelectedAsset) => void
  deselectExternalLink: (l: SelectedExternalLink) => void
  deselectItem: (item: ContentSelectorSelectedItem) => void
  deselectFilePages: (f: Pick<FolderFile, 'id'>) => void
  setAllowedIds: (ids: File['id'][]) => void
  setSelectedSection: (section: BrowserSectionType) => void
  setSearchQuery: (searchQuery: string) => void
  setAllowedOriginalExtensions: (extensions: string[]) => void
  setSearchMetadataFilters: (filters: Record<string, any>) => void
  setSortOption: (sortOption: SortOption) => void
  setFavorites: (favorites: Record<MiniPage['id'] | File['id'] | Folder['id'], Favorite>) => void
  setRecentFiles: (recentFiles: RecentFile[]) => void
  setFilteredItems: (filteredItems: FolderItemWithFolderId[]) => void
  fetchSectionData: () => Promise<void>
  setState: (newState: Partial<UnwrapNestedRefs<AppStore>>) => void
  isItemDisabled: (
    item:
      | SelectedFile
      | SelectedFolder
      | SelectedPage
      | RecentFileWithFavoriteId
      | FolderItemWithFolderId
      | FolderFileWithFolderId
      | (FolderFolder & { folder_id?: string; type: 'folder' }),
  ) => boolean
  setApiStore: (newApiStore: ApiStore) => void
  setIsLocalSearch: (isLocal: boolean) => void
  filterLocalItems: (query: string, filters: Record<string, any>) => void
}

export default function (apiStore?: ApiStore): AppStore {
  const launchDarklyFlags = inject<ComputedRef<any>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const state: AppStateProperties = {
    appName: ref(''),
    allowedContentTypes: ref([]),
    allowedIds: ref([]),
    initialMetadataFilters: ref({}),
    selectedSection: ref('all'),
    selectedItems: ref([]),
    addedItems: ref([]),
    selectedFiles: computed(() => state.selectedItems.value.filter(isFile)),
    selectedPages: computed(() => state.selectedItems.value.filter(isPage)),
    selectedAssets: computed(() => state.selectedItems.value.filter(isAsset)),
    selectedExternalLinks: computed(() => state.selectedItems.value.filter(isExternalLink)),
    activeSelectorType: ref('content'),
    allowedSelectorTypes: ref([]),
    search: ref(''),
    searchMetadataFilters: ref({}),
    allowedOriginalExtensions: ref<string[] | undefined>(),
    selectedSortOption: useLocalStorage<SortOption>('contentSelectorSortOption', 'nameAsc', {
      listenToStorageChanges: true,
    }),
    sectionPanelRef: ref(null),
    activeFolder: ref(null),
    activeFile: ref(null),
    disableNoSharable: ref(false),
    disableNoPptxData: ref(false),
    favorites: ref({}),
    recent: ref([]),
    filteredItems: ref([]),
    isLocalSearch: ref(false),
    allowMultimediaSelectorApp: ref(false),

    isAdmin: computed(() => state.appName.value === 'admin'),

    activeFolderFolders: computed(() => state.activeFolder.value?.folders ?? []),
    activeFolderFiles: computed(() => {
      const allowedTypes = state.allowedContentTypes.value
      let results: FolderFile[] = state.activeFolder.value?.files ?? []

      if (state.allowedOriginalExtensions.value?.length) {
        results = results.filter(
          (item) => item.original_extension && state.allowedOriginalExtensions.value?.includes(item.original_extension),
        )
      }

      if (allowedTypes?.length) {
        results = results.reduce<FolderFile[]>((acc, item) => {
          if (allowedTypes.indexOf(item.content_type) > -1) acc.push(item)

          return acc
        }, [])
      }

      return results
    }),
    isDragNDropEnabled: computed(() => {
      const isOnlyContentTab =
        state.allowedSelectorTypes.value.length === 0 ||
        (state.allowedSelectorTypes.value.length === 1 && state.allowedSelectorTypes.value?.[0] === 'content')

      return (
        launchDarklyFlags?.value?.enable_selectors_dnd && !state.allowMultimediaSelectorApp.value && isOnlyContentTab
      )
    }),
    showFilteredResults: computed(() => {
      // Always use filtered results when core distribution is enabled
      const isCoreDistributionEnabled = launchDarklyFlags?.value?.enable_file_core_distributions

      return (
        !!state.search.value ||
        Object.keys(state.searchMetadataFilters.value).length > 0 ||
        state.allowedIds.value.length > 0 ||
        isCoreDistributionEnabled
      )
    }),
    displayedFiles: computed(() => {
      const getItemsList = () => {
        switch (state.selectedSection.value) {
          case 'favorites':
            return state.displayedFavorites.value?.map((item: any) => ({
              ...item,
              tags: item?.file?.tags ?? [],
              type: item?.file ? 'file' : 'folder',
              content_type: item?.file?.content_type,
            }))
          case 'recent':
            return state.displayedRecentFiles.value

          case 'all':
            return state.showFilteredResults.value || state.allowedOriginalExtensions.value?.length
              ? (state.filteredItems.value as FolderItemWithFolderId[])
              : ([
                  ...state.activeFolderFolders.value.map((folder) => ({ ...folder, type: 'folder' })),
                  ...state.activeFolderFiles.value.map((file) => ({ ...file, type: 'file' })),
                ] as (SelectedFile | SelectedFolder)[])

          default:
            console.warn('Unknown content-selector section:', state.selectedSection.value)
            return []
        }
      }

      const isItemFileOrNotEmptyFolder = (item: SelectedFile | SelectedFolder) =>
        item.type === 'file' || (item.type === 'folder' && (item.files_count > 0 || item.folders_count > 0))

      const itemsList = getItemsList()
        .map((item) => {
          if (item.name?.startsWith('.')) {
            if (state.appName.value === 'impact') {
              return null // Hide item completely
            } else if (state.isAdmin.value) {
              return { ...item, opacity: 0.5 } // 50% visible
            }
          }
          return item
        })
        .filter((item): item is NonNullable<typeof item> => !!item && isItemFileOrNotEmptyFolder(item))

      return sortItems(itemsList, state.selectedSortOption.value)
    }),

    sortedFavorites: computed(() => {
      const folderComparator = (i: Favorite) => !!i.folder
      const fileComparator = (i: Favorite) => !!i.file && !i.page
      const pageComparator = (i: Favorite) => !!i.file && !!i.page
      const nameComparator = (i: Favorite) => i.name?.toLocaleLowerCase()

      return orderBy(
        Object.values(state.favorites.value),
        [folderComparator, fileComparator, pageComparator, nameComparator],
        ['desc', 'desc', 'desc', 'asc'],
      ) as Favorite[]
    }),

    displayedFavorites: computed(() => {
      return state.sortedFavorites.value.reduce(
        (acc, fav) => {
          const id = fav.page?.id ?? fav.file?.id ?? fav.folder?.id

          const searchQueryLower = state.search.value.toLocaleLowerCase()
          const matchesSearchQuery = id && fav.name?.toLocaleLowerCase().includes(searchQueryLower)

          if (matchesSearchQuery) {
            acc.push({
              ...fav,
              favoriteId: fav.id,
              id: id,
            })
          }
          return acc
        },
        [] as (Favorite & { favoriteId: string })[],
      )
    }),

    displayedRecentFiles: computed(() => {
      return state.recent.value
        .map((r) => ({
          ...r,
          favoriteId: state.favorites.value[r.id]?.id,
          type: ViewCompactItemType.File,
        }))
        .reduce((acc, recent) => {
          const id = recent.id

          const searchQueryLower = state.search.value.toLocaleLowerCase()
          const matchesSearchQuery = id && recent.name?.toLocaleLowerCase().includes(searchQueryLower)

          if (matchesSearchQuery) {
            acc.push(recent)
          }
          return acc
        }, [] as RecentFileWithFavoriteId[])
    }),
  }

  function deselectAllFiles(f: SelectedFile[] | Pick<FolderFile, 'id'>[]) {
    for (const item of f) {
      deselectFile(item)
    }
  }

  function deselectFile(f: SelectedFile | Pick<FolderFile, 'id'>) {
    state.selectedItems.value = state.selectedItems.value.filter((i) => isPage(i) || (isFile(i) && i.id !== f.id))
  }

  function deselectPage(p: SelectedPage) {
    state.selectedItems.value = state.selectedItems.value.filter(
      (i) => isFile(i) || (isPage(i) && (i.file.id !== p.file.id || i.page_index !== p.page_index)),
    )
  }

  function deselectAsset(a: SelectedAsset) {
    state.selectedItems.value = state.selectedItems.value.filter((i) => !(isAsset(i) && i.id === a.id))
  }

  function deselectExternalLink(l: SelectedExternalLink) {
    state.selectedItems.value = state.selectedItems.value.filter((i) => !(isExternalLink(i) && i.id === l.id))
  }

  function isItemDisabled(
    item:
      | SelectedFile
      | SelectedFolder
      | SelectedPage
      | RecentFileWithFavoriteId
      | FolderItemWithFolderId
      | FolderFileWithFolderId
      | (FolderFolder & { folder_id?: string; type: 'folder' }),
  ) {
    return (
      (state?.disableNoSharable?.value && !isShareable(item)) ||
      (state?.disableNoPptxData?.value && 'metadata' in item && !item?.metadata?.pptx_data)
    )
  }

  const actions = {
    getThumbnailUrl(file: Partial<File>) {
      if (file.content_type === 'video') {
        return file.thumbnail_url ?? null
      }

      return (
        file.thumbnail_url ??
        (SUPPORTED_IMAGE_TYPES.includes(file.content_extension ?? '') ? (file.content_url ?? null) : undefined)
      )
    },
    setAppName(appName: AppName): void {
      state.appName.value = appName
    },
    resetSelectedItems() {
      state.selectedItems.value = []
    },
    resetAddedItems() {
      state.addedItems.value = []
    },
    async setItems(variable: 'added' | 'selected', items: ContentSelectorSelectedItem[], maxSelections?: number) {
      if (!apiStore) return
      const { state: api } = apiStore

      if (variable === 'added') {
        state.addedItems.value = items
      } else {
        state.selectedItems.value = items
      }

      if (
        items.length === 1 &&
        isFile(items[0]) &&
        maxSelections === 1 &&
        isEmpty(state.initialMetadataFilters.value)
      ) {
        const file = await api.fileFetcher?.(items[0].id)

        if (file?.folder_id) {
          const folder = await api.folderItemsFetcher?.(file.folder_id)
          actions.setState({ activeFolder: folder })
          return
        }
      }

      const onlyPages = items.every((item) => isPage(item))
      const firstItemId = onlyPages && items.length > 0 ? items[0].file.id : null
      const onlyPagesOfSameFile = onlyPages && firstItemId && items.every((item) => item.file.id === firstItemId)

      if (onlyPagesOfSameFile) {
        const file = await api.fileFetcher?.(firstItemId)

        if (file?.folder_id && isEmpty(state.searchMetadataFilters.value)) {
          const folder = await api.folderItemsFetcher?.(file.folder_id)
          actions.setState({
            activeFile: file,
            ...(folder && { activeFolder: folder }),
          })
        } else {
          actions.setState({ activeFile: file })
        }
      }
    },
    setActiveSelectorType(s?: SelectorType) {
      if (s) state.activeSelectorType.value = s
    },
    setAllowedSelectorTypes(s: SelectorType[]) {
      state.allowedSelectorTypes.value = s
    },
    selectItem(i: ContentSelectorSelectedItem) {
      state.selectedItems.value = [...state.selectedItems.value, i]
    },
    handleItemClick(item: ContentSelectorSelectedItem) {
      if (state.addedItems.value.find((_item) => _item.id === item.id)) {
        state.addedItems.value = state.addedItems.value.filter((_item) => _item.id !== item.id)
      } else {
        state.addedItems.value.push(item)
      }
    },
    addItems(position: 'top' | 'bottom' | number = 'top') {
      let adjustedPosition = position
      const selectedIds = new Set(state.selectedItems.value.map((item) => item.id))

      const filteredAddedItems = state.addedItems.value.filter((addedItem, index) => {
        const hasItem = selectedIds.has(addedItem.id)

        if (hasItem && typeof adjustedPosition === 'number' && typeof position === 'number' && index < position) {
          adjustedPosition = Math.max(0, adjustedPosition - 1)
        }

        return !hasItem
      })

      if (typeof adjustedPosition === 'string') {
        state.addedItems.value =
          adjustedPosition === 'top'
            ? [...state.selectedItems.value, ...filteredAddedItems]
            : [...filteredAddedItems, ...state.selectedItems.value]
      } else {
        const result = filteredAddedItems
        result.splice(adjustedPosition ?? result.length, 0, ...state.selectedItems.value)
        state.addedItems.value = result
      }

      state.selectedItems.value = []

      nextTick(() => {
        const container = state.sectionPanelRef.value

        if (!container) return

        if (typeof adjustedPosition === 'number') {
          const targetElement = container.querySelectorAll('.selection-panel-list-item')[adjustedPosition]

          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        } else {
          container.scrollTo({
            top: position === 'top' ? 0 : container.scrollHeight,
            behavior: 'smooth',
          })
        }
      })
    },
    deleteItems() {
      const selectedIdSet = new Set(state.selectedItems.value.map((item) => item.id))
      state.addedItems.value = state.addedItems.value.filter((item) => !selectedIdSet.has(item.id))

      state.selectedItems.value = []
    },
    deleteItem(item: ContentSelectorSelectedItem) {
      state.addedItems.value = state.addedItems.value.filter((i) => i.id !== item.id)
    },
    setSectionPanelRef(element: HTMLElement | null) {
      state.sectionPanelRef.value = element
    },
    selectAllFiles(selection: SelectedFile[]) {
      for (const item of selection) {
        if (!isItemDisabled(item) && item.type === 'file' && !state.selectedItems.value.some((i) => i.id === item.id))
          actions.selectItem(item)
      }
    },
    setDisableNoSharable(disable: boolean) {
      state.disableNoSharable.value = disable
    },
    setDisableNoPptxData(disable: boolean) {
      state.disableNoPptxData.value = disable
    },
    deselectAllFiles,
    deselectFile,
    deselectPage,
    deselectAsset,
    deselectExternalLink,
    deselectItem(item: ContentSelectorSelectedItem) {
      if (isFile(item)) {
        deselectFile(item)
      } else if (isPage(item)) {
        deselectPage(item)
      } else if (isAsset(item)) {
        deselectAsset(item)
      } else if (isExternalLink(item)) {
        deselectExternalLink(item)
      }
    },
    deselectFilePages(f: Pick<FolderFile, 'id'>) {
      state.selectedItems.value = state.selectedItems.value.filter(
        (i) => isFile(i) || (isPage(i) && i.file.id !== f.id),
      )
    },
    setAllowedIds(ids: File['id'][]) {
      state.allowedIds.value = ids
    },
    setSelectedSection(section: BrowserSectionType): void {
      state.activeFile.value = null
      if (section !== state.selectedSection.value) {
        state.selectedSection.value = section
        actions.fetchSectionData()
      }
    },
    setSearchQuery(searchQuery: string): void {
      state.search.value = searchQuery
    },
    setAllowedOriginalExtensions(extensions: string[]): void {
      state.allowedOriginalExtensions.value = extensions
    },
    setSortOption(sortOption: SortOption): void {
      state.selectedSortOption.value = sortOption
    },
    setSearchMetadataFilters(filters: Record<string, any>): void {
      state.searchMetadataFilters.value = filters
    },
    setFavorites(favorites: Record<MiniPage['id'] | File['id'] | Folder['id'], Favorite>): void {
      state.favorites.value = favorites
    },
    setRecentFiles(recentFiles: RecentFile[]): void {
      state.recent.value = recentFiles
    },
    setFilteredItems(filteredItems: FolderItemWithFolderId[]): void {
      state.filteredItems.value = filteredItems
    },
    async fetchSectionData() {
      if (!apiStore) return
      const { state: api } = apiStore

      switch (state.selectedSection.value) {
        case 'favorites':
          api.favoritesFetcher?.()
          break

        case 'recent':
          api.recentFilesFetcher?.()
          break

        case 'all':
          if (state.activeFile.value) {
            state.activeFile.value = (await api.fileFetcher?.(state.activeFile.value.id)) || null
          } else if (state.activeFolder.value) {
            state.activeFile.value = null
            state.activeFolder.value = (await api.folderItemsFetcher?.(state.activeFolder.value?.id)) || null
          }
          break

        default:
          console.warn('Unknown content-selector section:', state.selectedSection.value)
      }
    },
    setState(newState: Partial<UnwrapNestedRefs<AppStateProperties>>) {
      Object.keys(newState).forEach((key) => {
        const k = key as keyof AppStateProperties

        if (['selectedPages', 'selectedFiles'].includes(k)) throw new Error(`Cannot set ${k} directly`)
        if (state[k] === undefined) throw new Error(`Unknown state key: ${k}`)
        // Normalize undefined to null
        const value = newState[k] === undefined ? null : newState[k]
        ;(state[k].value as any) = value
      })
    },
    isItemDisabled,
    setIsLocalSearch(isLocal: boolean) {
      state.isLocalSearch.value = isLocal
    },
    filterLocalItems(query: string, filters: Record<string, any>) {
      const normalizedQuery = query.toLowerCase()
      const hasMetadataFilters = Object.keys(filters).length > 0

      // Filter folders - folders should always be included if they match the search query
      // Metadata filters don't apply to folders since folders don't have metadata
      const filteredFolders: FolderItemWithFolderId[] = state.activeFolderFolders.value
        .filter((folder) => folder.name?.toLowerCase().includes(normalizedQuery))
        .map(
          (folder): FolderItemWithFolderId => ({
            ...folder,
            type: 'folder' as const,
            folder_id: state.activeFolder.value?.id,
          }),
        )

      // Filter files - both search query and metadata filters apply to files
      const filteredFiles: FolderItemWithFolderId[] = state.activeFolderFiles.value
        .filter((file) => {
          const nameMatch = file.name?.toLowerCase().includes(normalizedQuery)
          const createdByMatch =
            file.created_by?.name?.toLowerCase().includes(normalizedQuery) ||
            file.created_by?.email?.toLowerCase().includes(normalizedQuery)
          const tagMatch = file.tags?.some((tag) => tag.toLowerCase().includes(normalizedQuery))

          let metadataMatch = true
          if (hasMetadataFilters) {
            for (const key in filters) {
              const fileValue = (file as any).metadata?.[key]
              const filterValue = filters[key]
              if (
                filterValue &&
                (!fileValue || !String(fileValue).toLowerCase().includes(String(filterValue).toLowerCase()))
              ) {
                metadataMatch = false
                break
              }
            }
          }

          return (nameMatch || createdByMatch || tagMatch) && metadataMatch
        })
        .map(
          (file): FolderItemWithFolderId =>
            ({
              ...file,
              type: 'file' as const,
              folder_id: state.activeFolder.value?.id,
            }) as FolderFileWithFolderId & { type: 'file' },
        )

      // Always include both folders and files in results for consistent UX
      // Folders that match should always be visible regardless of metadata filters
      const results: FolderItemWithFolderId[] = [...filteredFolders, ...filteredFiles]
      actions.setFilteredItems(results)
    },
  }

  return {
    ...state,
    ...actions,
    setApiStore(newApiStore: ApiStore) {
      apiStore = newApiStore
    },
  }
}
